import React, { useEffect, useMemo, useState } from 'react';

import useServices from '../../../hooks/useServices';
import { isAxiosError } from 'axios';
import ServicesTable from '../../../components/ServicesTable/ServicesTable';
import { GetServicesQuery } from '@hud/portal-dtos/webapp-service.dto';
import Page from '../../../components/Page/Page';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import useTimeFrame from '../../../hooks/useTimeFrame';
import useEnvironment from '../../../hooks/useEnvironment';
import useAnalytics from '../../../hooks/useAnalytics';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import { DEFAULT_PAGE_SIZE } from '../../../consts';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import { useQueryParamState } from '../../../hooks/useQueryParamState';

export default function ServicesPage() {
  const analytics = useAnalytics();
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const { userPreferredServices, updateUserPreferences } = useUserPreferences();

  const [totalPages, setTotalPages] = useState<number>(0);

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<CommonTableQueryParams>({
      search: '',
      page: 1,
      orderBy: 'lastSeenAt',
      desc: true,
    });

  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
  });

  const handleSelectUserServices = (ids: number[], isSingleService: boolean) => {
    let newSelectedServices: number[];

    if (isSingleService) {
      const serviceName = servicesQuery.data?.services.find(
        (service) => service.id === ids[0],
      )?.name;
      const currentSelectedServices = userPreferredServices ?? [];
      if (currentSelectedServices.includes(ids[0])) {
        newSelectedServices = currentSelectedServices.filter(
          (serviceId: number) => serviceId !== ids[0],
        );
        analytics.capture('service_removed_from_my_services', {
          service_name: serviceName ?? '',
        });
      } else {
        newSelectedServices = [...currentSelectedServices, ids[0]];
        analytics.capture('service_added_to_my_services', {
          service_name: serviceName ?? '',
        });
      }
    } else {
      newSelectedServices = ids;
      if (ids.length === 0) {
        analytics.capture('services_removed_all_from_my_services');
      } else {
        analytics.capture('services_added_all_to_my_services');
      }
    }

    updateUserPreferences({ selectedServices: newSelectedServices });
  };

  const servicesQueryParams = useMemo(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset: (tableState.page || 1) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE,
      searchTerms: tableState.search.split(' ').filter(Boolean),
      orderBy: tableState.orderBy as GetServicesQuery['orderBy'],
      timeFrame,
      environmentName: environment?.name,
    }),
    [
      tableState.page,
      tableState.search,
      tableState.orderBy,
      timeFrame,
      environment?.name,
    ],
  );

  const servicesQuery = useServices(servicesQueryParams);

  useEffect(() => {
    document.title = 'Services • Hud';
  }, []);

  useEffect(() => {
    if (servicesQuery.isSuccess) {
      const totalPages = Math.ceil(servicesQuery.data.countFiltered / DEFAULT_PAGE_SIZE);
      setTotalPages(totalPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesQuery.data, servicesQuery.isSuccess]);

  // Analytics
  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('services_viewed', {
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.name]);

  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('services_searched', {
      search: tableState.search,
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.search]);

  useEffect(() => {
    if (servicesQuery.isSuccess) {
      analytics.capture('services_paginated', {
        page: tableState.page,
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        search: tableState.search,
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.page]);

  useEffect(() => {
    if (servicesQuery.isSuccess) {
      analytics.capture('services_sorted', {
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.orderBy.key, tableState.orderBy.sortOrder]);

  if (servicesQuery.isError) {
    if (!isAxiosError(servicesQuery.error)) {
      return (
        <div>
          <div>Something went wrong</div>
        </div>
      );
    }
    const error = servicesQuery.error.response?.data;
    return <div>{error?.message ?? 'Something went wrong'}</div>;
  }

  return (
    <Page
      header="Services"
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <ServicesTable
        services={servicesQuery.data?.services ?? []}
        loading={servicesQuery.isLoading || environment?.name === null}
        total={servicesQuery.data?.total}
        countActiveFiltered={servicesQuery.data?.countActiveFiltered}
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        pages={totalPages}
        currentPage={totalPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        userPreferredServices={userPreferredServices ?? []}
        onSavePreferredServices={handleSelectUserServices}
        fullPageTable
      />
    </Page>
  );
}
